import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { DataTable } from "@/components/ui/data-table";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { PageHeader, PageHeaderHeading } from "@/components/ui/page-header";
import {
  FilterCard,
  FilterSearch,
  FilterActions,
} from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ReactSelect } from "@/components/ui/react-select";
import {
  Search,
  RotateCcw,
  Package,
  ScanBarcode,
  Store,
  History,
  AlertCircle,
} from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";

import SerialDetailModal from "./components/SerialDetailModal";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn, formatDate } from "@/lib/utils";
import RecordNotFound from "@/core/components/RecordNotFound";

const IS_SUPERMARKET = import.meta.env.VITE_APP_PROFILE === "supermarket";

const SerialTrack = () => {
  const permissions = useSelector((state) => state.userpermission_data) || [];
  const currentStore = useSelector((state) => state.current_store);
  const defaultStoreId = currentStore?.id ? String(currentStore.id) : "";

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  const [searchParams] = useSearchParams();
  const [searchText, setSearchText] = useState(
    searchParams.get("serial") || searchParams.get("search") || "",
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [storeId, setStoreId] = useState(defaultStoreId);
  const [status, setStatus] = useState("");
  const [productId, setProductId] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [selectedSerial, setSelectedSerial] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Reset page when filters change
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentPage(1);
  }, [searchText, storeId, status, productId]);

  const statusOptions = [
    { value: "", label: "All Statuses" },
    { value: "available", label: "Available" },
    { value: "sold", label: "Sold" },
    { value: "damaged", label: "Damaged" },
    { value: "lost", label: "Lost" },
    { value: "expired", label: "Expired" },
    { value: "returned", label: "Returned" },
    { value: "under_inspection", label: "Under Inspection" },
    { value: "refurbished", label: "Refurbished" },
  ];

  // Queries
  const {
    data: serialsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      "inventory-serials",
      currentPage,
      pageSize,
      searchText,
      storeId,
      status,
      productId,
    ],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.SERIALS, {
        params: {
          page: currentPage,
          per_page: pageSize,
          search: searchText || null,
          store_id: storeId || null,
          status: status || null,
          product_id: productId || null,
          with: "product,store,inventory",
        },
      });
      return res.data;
    },
    enabled:
      hasPermission("inventory serial track") ||
      hasPermission("inventory serial read"),
  });

  const { data: stores = [] } = useQuery({
    queryKey: ["stores"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.STORES.STORES);
      return res.data.data || [];
    },
    enabled: hasPermission("store read"),
  });

  const { data: products = [] } = useQuery({
    queryKey: ["all-pos-products", productSearch],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.POS_PRODUCTS, {
        params: {
          per_page: 20,
          search: productSearch || undefined,
        },
      });

      return res.data.data || [];
    },
    enabled: hasPermission("product read"),
    keepPreviousData: true,
  });

  const serials = serialsData?.data || [];
  const paginationMeta = {
    current_page:
      serialsData?.meta?.current_page || serialsData?.current_page || 1,
    last_page: serialsData?.meta?.last_page || serialsData?.last_page || 1,
    per_page: serialsData?.meta?.per_page || serialsData?.per_page || pageSize,
    total: serialsData?.meta?.total || serialsData?.total || 0,
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "available":
        return "success";
      case "sold":
        return "secondary";
      case "damaged":
        return "destructive";
      case "lost":
        return "destructive";
      case "expired":
        return "destructive";
      case "returned":
        return "warning";
      case "under_inspection":
        return "warning";
      case "refurbished":
        return "outline";
      default:
        return "secondary";
    }
  };

  const columns = [
    {
      accessorKey: "serial_number",
      header: "Serial Number",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <ScanBarcode className="h-4 w-4 text-primary" />
          <span className="font-bold font-mono tracking-tight text-primary">
            {row.original.serial_number}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "product.name",
      header: "Product",
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium text-foreground">
            {row.original.product?.name || "-"}
          </span>
          <span className="text-[10px] text-muted-foreground uppercase tracking-widest">
            {row.original.product?.sku}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "store.name",
      header: "Current Store",
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Store size={14} className="opacity-70" />
          <span className="text-sm font-medium">
            {row.original.store?.name || "-"}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const info = row.original.status_info;

        return (
          <Badge variant={info?.variant || getStatusColor(row.original.status)}>
            {info?.label || row.original.status}
          </Badge>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: "Date Added",
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground font-medium">
          {row.original.created_at
            ? formatDate(new Date(row.original.created_at))
            : "-"}
        </span>
      ),
    },
    ...(IS_SUPERMARKET
      ? [
        {
          accessorKey: "expiry_date",
          header: "Expiry Date",
          cell: ({ row }) => {
            if (!row.original.expiry_date)
              return (
                <span className="text-xs text-muted-foreground font-medium">
                  -
                </span>
              );
            return (
              <div className="flex flex-col">
                <span
                  className={cn(
                    "text-xs font-medium",
                    row.original.is_expired
                      ? "text-destructive"
                      : "text-foreground",
                  )}
                >
                  {formatDate(new Date(row.original.expiry_date))}
                </span>
                {row.original.days_until_expiry !== null && (
                  <span
                    className={cn(
                      "text-[10px] uppercase tracking-widest",
                      row.original.is_expired
                        ? "text-destructive/80"
                        : "text-muted-foreground",
                    )}
                  >
                    {row.original.is_expired
                      ? "Expired"
                      : `${row.original.days_until_expiry} days left`}
                  </span>
                )}
              </div>
            );
          },
        },
      ]
      : []),
    {
      id: "actions",
      header: "History",
      cell: ({ row }) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-primary transition-colors"
                onClick={() => {
                  setSelectedSerial(row.original);
                  setIsDetailOpen(true);
                }}
              >
                <History size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>View movement history</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },
  ];

  const handleReset = () => {
    setSearchText("");
    setStoreId(defaultStoreId);
    setStatus("");
    setProductId("");
    setCurrentPage(1);
  };

  if (!hasPermission("inventory serial track")) {
    return <RecordNotFound />;
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Serial Number Tracking"
          subtitle="Lookup and track individual products via serial numbers across all stores"
        />
      </PageHeader>

      <FilterCard>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 w-full">
          <div className="md:col-span-1">
            <FilterSearch
              placeholder="Search SN, SKU..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          <div className="w-full">
            <ReactSelect
              onInputChange={(val) => setProductSearch(val)}
              options={products.map((p) => ({
                value: String(p.id),
                label: p.name,
                sku: p.sku,
              }))}
              value={
                productId
                  ? {
                    value: productId,
                    label: products.find((p) => String(p.id) === productId)
                      ?.name,
                  }
                  : null
              }
              onChange={(opt) => setProductId(opt?.value || "")}
              placeholder="Filter by Product"
              isClearable
            />
          </div>

          <div className="w-full">
            <ReactSelect
              options={stores.map((s) => ({
                value: String(s.id),
                label: s.name,
              }))}
              value={
                storeId
                  ? {
                    value: storeId,
                    label: stores.find((s) => String(s.id) === storeId)?.name,
                  }
                  : null
              }
              onChange={(opt) => setStoreId(opt?.value || "")}
              placeholder="Filter by Store"
              isClearable
            />
          </div>

          <div className="w-full">
            <ReactSelect
              options={statusOptions}
              value={statusOptions.find((opt) => opt.value === status)}
              onChange={(opt) => setStatus(opt?.value || "")}
              placeholder="All Statuses"
              isClearable
            />
          </div>

          <FilterActions>
            <Button
              variant="outline"
              onClick={handleReset}
              className="flex items-center gap-2 h-10 w-full md:w-auto"
            >
              <RotateCcw size={16} />
              Reset
            </Button>
            <Button
              variant="default"
              className="flex items-center gap-2 h-10 shadow-sm w-full md:w-auto"
              onClick={() => refetch()}
            >
              <Search size={16} />
              Search
            </Button>
          </FilterActions>
        </div>
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={serials}
          isLoading={isLoading}
          footerInfo={
            <span className="font-medium text-foreground">
              Total Serials: {paginationMeta.total || 0}
            </span>
          }
          pagination={{
            pageIndex: currentPage - 1,
            pageSize: pageSize,
          }}
          onPaginationChange={(updater) => {
            if (typeof updater === "function") {
              const newState = updater({
                pageIndex: currentPage - 1,
                pageSize,
              });
              setCurrentPage(newState.pageIndex + 1);
              setPageSize(newState.pageSize);
            } else {
              setCurrentPage(updater.pageIndex + 1);
              setPageSize(updater.pageSize);
            }
          }}
          pageCount={paginationMeta.last_page}
        />
      </TableCard>

      {/* Info Banner */}
      {!isLoading && serials.length === 0 && searchText && (
        <div className="mt-6 p-6 rounded-2xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/30 flex items-start gap-4">
          <div className="p-2 bg-amber-100 dark:bg-amber-900/40 rounded-lg text-amber-600 dark:text-amber-400 shrink-0">
            <AlertCircle size={24} />
          </div>
          <div className="space-y-1">
            <h4 className="font-bold text-amber-900 dark:text-amber-100 italic">
              No exact match found
            </h4>
            <p className="text-sm text-amber-800/80 dark:text-amber-200/60 leading-relaxed">
              We couldn't find any serial numbers matching "
              <strong>{searchText}</strong>". Try searching for a partial
              serial, product SKU, or ensure you've selected the correct store.
            </p>
          </div>
        </div>
      )}

      <SerialDetailModal
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        serialId={selectedSerial?.id}
        serialNumber={selectedSerial?.serial_number}
      />
    </PageContainer>
  );
};

export default SerialTrack;

